import React, { useState, useRef } from 'react'
import { ClassId, HeroId } from '../types'
import { CLASS_DATA, getHeroDescription, getClassDescription } from '../data/heroData'
import { isTouchDevice } from '../services/device'
import { Language, t } from '../data/translations'
import { useTouchHint } from '../hooks/useTouchHint'
// ... existing imports ...
import verisk from '../assets/custom/Verisk.png'
import veriskAvatar from '../assets/custom/VeriskAvatar.png'
import verg from '../assets/custom/Verg.png'
import vergAvatar from '../assets/custom/VergAvatar.png'
import astrid from '../assets/custom/Astrid.png'
import astridAvatar from '../assets/custom/AstridAvatar.png'
import warrior from '../assets/custom/Warrior.png'
import mage from '../assets/custom/Mage.png'
import occultist from '../assets/custom/Occultist.png'
import ferrim from '../assets/custom/Ferrim.png'
import ferrimAvatar from '../assets/custom/FerrimAvatar.png'
import norim from '../assets/custom/Norim.png'
import norimAvatar from '../assets/custom/NorimAvatar.png'
import evelion from '../assets/custom/Evelion.png'
import evelionAvatar from '../assets/custom/EvelionAvatar.png'
import coril from '../assets/custom/Coril.png'
import corilAvatar from '../assets/custom/CorilAvatar.png'
import terrik from '../assets/custom/Terrik.png'
import terrikAvatar from '../assets/custom/TerrikAvatar.png'
import nyrix from '../assets/custom/Nyrix.png'
import nyrixAvatar from '../assets/custom/NyrixAvatar.png'
import shanuFull from '../assets/custom/Shanu.png'
import shanuAvatar from '../assets/custom/ShanuAvatar.png'
import lorianFull from '../assets/custom/Lorian.png'
import lorianAvatar from '../assets/custom/LorianAvatar.png'
import gemataAvatar from '../assets/custom/GemataAvatar.png'
import gemataFull from '../assets/custom/Gemata.png'
import trifleAvatar from '../assets/custom/TrifleAvatar.png'
import trifleFull from '../assets/custom/Trifle.png'
import axelAvatar from '../assets/custom/AxelAvatar.png'
import axelFull from '../assets/custom/Axel.png'

type Props = {
  onChoose: (hero: HeroId, playerClass: ClassId) => void
  otherPlayerName: string
  otherActionText: string
  availableHeroes?: HeroId[]
  forceHero?: HeroId
  language: Language
}

const HERO_DATA: { id: HeroId; name: string; avatar: string; full: string }[] = [
  { id: 'VERISK', name: 'Verisk', avatar: veriskAvatar, full: verisk },
  { id: 'VERG', name: 'Verg', avatar: vergAvatar, full: verg },
  { id: 'ASTRID', name: 'Astrid', avatar: astridAvatar, full: astrid },
  { id: 'FERRIM', name: 'Ferrim', avatar: ferrimAvatar, full: ferrim },
  { id: 'NORIM', name: 'Norim', avatar: norimAvatar, full: norim },
  { id: 'EVELION', name: 'Evelion', avatar: evelionAvatar, full: evelion },
  { id: 'CORIL', name: 'Coril', avatar: corilAvatar, full: coril },
  { id: 'TERRIK', name: 'Terrik', avatar: terrikAvatar, full: terrik },
  { id: 'NYRIX', name: 'Nyrix', avatar: nyrixAvatar, full: nyrix },
  { id: 'SHANU', name: 'Shanu', avatar: shanuAvatar, full: shanuFull },
  { id: 'LORIAN', name: 'Lorian', avatar: lorianAvatar, full: lorianFull },
  { id: 'GEMATA', name: 'Gemata', avatar: gemataAvatar, full: gemataFull },
  { id: 'TRIFLE', name: 'Trifle', avatar: trifleAvatar, full: trifleFull },
  { id: 'AXEL', name: 'Axel', avatar: axelAvatar, full: axelFull },
]

const HeroClassChoiceModal: React.FC<Props> = ({ onChoose, otherPlayerName, otherActionText, availableHeroes, forceHero, language }) => {
  const [selectedHero, setSelectedHero] = useState<HeroId | null>(forceHero || null)
  const [selectedClass, setSelectedClass] = useState<ClassId | null>(null)
  const [heroDetailOpen, setHeroDetailOpen] = useState(!!forceHero)
  const [classDetailOpen, setClassDetailOpen] = useState(false)

  // Hover tooltip state for hero descriptions
  const [hoveredHero, setHoveredHero] = useState<{ id: HeroId; name: string; full: string } | null>(null)
  const [hoveredClass, setHoveredClass] = useState<{ id: string; name: string; icon: string; desc: string } | null>(null)
  const [hintTrigger, setHintTrigger] = useState<'hover' | 'longpress' | null>(null)
  const hoverTimer = useRef<NodeJS.Timeout | null>(null)

  const handleHeroMouseEnter = (hero: { id: HeroId; name: string; full: string }) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => {
      setHoveredHero(hero)
      setHoveredClass(null)
      setHintTrigger('hover')
    }, 440)
  }

  const handleHeroMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoveredHero(null)
  }

  const handleClassMouseEnter = (classData: { id: string; name: string; icon: string; desc: string }) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => {
      setHoveredClass(classData)
      setHoveredHero(null)
      setHintTrigger('hover')
    }, 440)
  }

  const handleClassMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoveredClass(null)
  }

  const canConfirm = !!selectedHero && !!selectedClass
  const visibleHeroes = HERO_DATA.filter(h => !availableHeroes || availableHeroes.includes(h.id))

  // Helper component for touch-aware buttons
  const TouchAwareButton: React.FC<{
    onTap: () => void
    onLongPress: () => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    className?: string
    children: React.ReactNode
  }> = ({ onTap, onLongPress, onMouseEnter, onMouseLeave, className, children }) => {
    const touch = useTouchHint({ onLongPress })
    return (
      <button
        {...touch}
        onClick={(e) => {
          if (touch.interceptClick(e)) return
          onTap()
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={className}
      >
        {children}
      </button>
    )
  }

  return (
    <div className="hero-selection-modal absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-40">
      <h2 className="text-4xl font-bold mb-6 text-slate-300 text-shadow-lg">{t('ui.chooseHeroAndClass', language)}</h2>
      <div className="mb-6 text-slate-300">{otherPlayerName} {t(otherActionText as any, language)}</div>

      <div className="grid grid-cols-2 gap-10 items-start h-[500px]">
        {/* Classes Section */}
        <div className="flex flex-col items-center h-full">
          <div className="text-xl font-bold mb-3 text-slate-200">{t('ui.classes', language)}</div>
          {!classDetailOpen && (
            <div className="flex gap-4">
              {Object.values(CLASS_DATA).map(classItem => (
                <TouchAwareButton
                  key={classItem.id}
                  onTap={() => { setSelectedClass(classItem.id as ClassId); setClassDetailOpen(true); setHoveredClass(null) }}
                  onLongPress={() => {
                    setHoveredClass({ ...classItem, name: t(`class.${classItem.id}.name` as any, language), desc: getClassDescription(classItem.id, language) });
                    setHintTrigger('longpress');
                  }}
                  onMouseEnter={() => handleClassMouseEnter({ ...classItem, name: t(`class.${classItem.id}.name` as any, language), desc: getClassDescription(classItem.id, language) })}
                  onMouseLeave={handleClassMouseLeave}
                  className="p-3 bg-slate-800/70 rounded-lg border-2 border-slate-600 hover:border-slate-400 transition flex flex-col items-center"
                >
                  <img src={classItem.icon} alt={classItem.name} className="w-24 h-24 object-contain rounded" />
                  <div className="mt-2 text-slate-300 text-sm font-bold text-center">{t(`class.${classItem.id}.name` as any, language)}</div>
                </TouchAwareButton>
              ))}
            </div>
          )}
          {classDetailOpen && (
            <div className="flex flex-col items-center">
              <div className="p-4 bg-slate-800/70 rounded-lg border-2 border-slate-600">
                <img src={selectedClass === 'MAGE' ? mage : (selectedClass === 'WARRIOR' ? warrior : occultist)} alt="Class" className="w-64 h-64 object-contain rounded" />
              </div>
              <div className="mt-3 flex gap-3">
                <button onClick={() => { setClassDetailOpen(false); setSelectedClass(null) }} className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-lg transition">{t('ui.cancel', language)}</button>
              </div>
            </div>
          )}
        </div>

        {/* Heroes Section */}
        <div className="flex flex-col items-center h-full">
          <div className="text-xl font-bold mb-3 text-slate-200">{t('ui.heroes', language)}</div>
          {!heroDetailOpen && (
            <div className="w-[400px] max-h-[400px] overflow-y-auto pr-2 custom-scrollbar" style={{ touchAction: 'pan-y' }}>
              <style>{`.custom-scrollbar::-webkit-scrollbar { width: 8px; } .custom-scrollbar::-webkit-scrollbar-track { background: rgba(30, 41, 59, 0.5); border-radius: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(to bottom, #6366f1, #a855f7); border-radius: 4px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: linear-gradient(to bottom, #4f46e5, #9333ea); }`}</style>
              <div className="grid grid-cols-3 gap-4">
                {visibleHeroes.map(hero => (
                  <TouchAwareButton
                    key={hero.id}
                    onTap={() => { setSelectedHero(hero.id); setHeroDetailOpen(true); setHoveredHero(null) }}
                    onLongPress={() => { setHoveredHero(hero); setHintTrigger('longpress') }}
                    onMouseEnter={() => handleHeroMouseEnter(hero)}
                    onMouseLeave={handleHeroMouseLeave}
                    className="p-3 bg-slate-800/70 rounded-lg border-2 border-slate-600 hover:border-slate-400 transition flex flex-col items-center"
                  >
                    <img src={hero.avatar} alt={hero.name} className="w-20 h-20 object-cover rounded" />
                    <div className="mt-2 text-slate-300 text-sm font-bold text-center">{t(`hero.${hero.id}.name` as any, language)}</div>
                  </TouchAwareButton>
                ))}
              </div>
            </div>
          )}
          {heroDetailOpen && selectedHero && (
            <div className="flex flex-col items-center">
              <div className="p-2 bg-slate-800/70 rounded-lg border-2 border-slate-600">
                <img src={HERO_DATA.find(h => h.id === selectedHero)?.full} alt="Hero" className="w-[320px] h-[420px] object-contain rounded" />
              </div>
              <div className="mt-3 flex gap-3">
                <button onClick={() => { setHeroDetailOpen(false); setSelectedHero(null) }} className="bg-red-600 hover:bg-red-500 text-white font-bold py-2 px-4 rounded-lg transition">{t('ui.cancel', language)}</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero Hover Tooltip */}
      {hoveredHero && !heroDetailOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center animate-[fadeIn_0.2s_ease-out] ${hintTrigger === 'longpress' ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
          onClick={() => hintTrigger === 'longpress' && setHoveredHero(null)}
        >
          {/* Full-screen tap-to-dismiss overlay for touch */}
          <div className="absolute inset-0 z-[-1]" />
          <div className="bg-black/90 backdrop-blur-sm rounded-xl border-2 border-slate-600 p-4 shadow-2xl flex items-center gap-4 pointer-events-auto">
            <img src={hoveredHero.full} className="h-56 rounded-lg shadow-lg" alt={hoveredHero.name} />
            <div className="max-w-xs">
              <h3 className="text-2xl font-bold text-white mb-2">{t(`hero.${hoveredHero.id}.name` as any, language)}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{getHeroDescription(hoveredHero.id, language)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Class Hover Tooltip */}
      {hoveredClass && !classDetailOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center animate-[fadeIn_0.2s_ease-out] ${hintTrigger === 'longpress' ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
          onClick={() => hintTrigger === 'longpress' && setHoveredClass(null)}
        >
          {/* Full-screen tap-to-dismiss overlay for touch */}
          <div className="absolute inset-0 z-[-1]" />
          <div className="bg-black/90 backdrop-blur-sm rounded-xl border-2 border-slate-600 p-4 shadow-2xl flex items-center gap-4 pointer-events-auto">
            <div className="w-32 h-32 p-4 flex items-center justify-center">
              <img src={hoveredClass.icon} className="w-full h-full object-contain" alt={hoveredClass.name} />
            </div>
            <div className="max-w-xs">
              <h3 className="text-2xl font-bold text-white mb-2">{hoveredClass.name}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{hoveredClass.desc}</p>
            </div>
          </div>
        </div>
      )}

      <button
        disabled={!canConfirm}
        onClick={() => canConfirm && onChoose(selectedHero!, selectedClass!)}
        className={`mt-8 font-bold py-2 px-6 rounded-lg transition-colors duration-300 ${canConfirm ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}`}
      >
        {t('ui.confirm', language)}
      </button>
    </div>
  )
}

export default HeroClassChoiceModal